// "By the numbers" — six objective stats derived live from the polls
// dataset. No commentary, no interpretation; the cards just surface
// facts the user could derive themselves with a calculator.

import { useMemo } from 'react';
import type { Poll, PollsterName } from '../../data/surveys.types';
import { POLLSTERS } from '../../data/surveys';
import { usePolls } from '../../lib/usePolls';
import './Surveys.css';

interface Stat {
  value: string;
  label: string;
  meta?: string;
}

const MS_PER_DAY = 86_400_000;
const isAnchor = (p: Poll): boolean =>
  p.pollster === '2024 EP Election (actual result)';

function shortPollster(name: PollsterName): string {
  return POLLSTERS[name].abbr;
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-GB', {
    month: 'short',
    year: 'numeric',
  });
}

export default function ByTheNumbers() {
  const { polls } = usePolls();

  const stats = useMemo<Stat[]>(() => {
    const surveys = polls.filter((p) => !isAnchor(p));
    if (surveys.length === 0) {
      return [];
    }
    const sorted = [...surveys].sort((a, b) => a.date.localeCompare(b.date));

    // 1. Survey count
    const total = surveys.length;

    // 2. Most active pollster
    const counts = new Map<PollsterName, number>();
    for (const p of surveys) {
      counts.set(p.pollster, (counts.get(p.pollster) ?? 0) + 1);
    }
    const [topPollster, topCount] = [...counts.entries()].sort(
      (a, b) => b[1] - a[1],
    )[0];

    // 3. Biggest single-pollster swing (consecutive polls of same firm)
    let biggestSwing = { delta: 0, fromDate: '', toDate: '', pollster: '' as PollsterName };
    const byPollster = new Map<PollsterName, Poll[]>();
    for (const p of sorted) {
      const list = byPollster.get(p.pollster) ?? [];
      list.push(p);
      byPollster.set(p.pollster, list);
    }
    for (const [name, list] of byPollster) {
      for (let i = 1; i < list.length; i++) {
        const delta = Math.abs(list[i].lead - list[i - 1].lead);
        if (delta > biggestSwing.delta) {
          biggestSwing = {
            delta,
            fromDate: list[i - 1].date,
            toDate: list[i].date,
            pollster: name,
          };
        }
      }
    }

    // 4. Largest gap between pollsters within a 30-day window
    let biggestDisagreement = {
      delta: 0,
      a: null as Poll | null,
      b: null as Poll | null,
    };
    for (let i = 0; i < sorted.length; i++) {
      for (let j = i + 1; j < sorted.length; j++) {
        const a = sorted[i];
        const b = sorted[j];
        if (a.pollster === b.pollster) continue;
        const days =
          (Date.parse(b.date) - Date.parse(a.date)) / MS_PER_DAY;
        if (days > 30) break; // sorted ascending, further js are even larger
        const delta = Math.abs(a.lead - b.lead);
        if (delta > biggestDisagreement.delta) {
          biggestDisagreement = { delta, a, b };
        }
      }
    }

    // 5. Highest PL lead in the dataset
    const high = [...sorted].sort((a, b) => b.lead - a.lead)[0];

    // 6. Longest stretch without a published poll (any pollster)
    let longestGap = 0;
    let gapEndDate = '';
    for (let i = 1; i < sorted.length; i++) {
      const days = Math.round(
        (Date.parse(sorted[i].date) - Date.parse(sorted[i - 1].date)) /
          MS_PER_DAY,
      );
      if (days > longestGap) {
        longestGap = days;
        gapEndDate = sorted[i].date;
      }
    }

    const out: Stat[] = [
      {
        value: String(total),
        label: 'Surveys in scope',
        meta: 'since the EP 2024 baseline',
      },
      {
        value: String(topCount),
        label: 'Polls by the most active firm',
        meta: shortPollster(topPollster),
      },
      {
        value: `${biggestSwing.delta.toFixed(1)}pp`,
        label: 'Biggest single-pollster swing',
        meta: `${shortPollster(biggestSwing.pollster as PollsterName)}, ${fmtDate(biggestSwing.fromDate)} → ${fmtDate(biggestSwing.toDate)}`,
      },
    ];
    if (biggestDisagreement.a && biggestDisagreement.b) {
      out.push({
        value: `${biggestDisagreement.delta.toFixed(1)}pp`,
        label: 'Largest gap between pollsters within 30 days',
        meta: `${shortPollster(biggestDisagreement.a.pollster)} vs ${shortPollster(biggestDisagreement.b.pollster)}, ${fmtDate(biggestDisagreement.a.date)}`,
      });
    }
    if (high) {
      out.push({
        value: `+${high.lead.toFixed(1)}pp`,
        label: 'Highest PL lead recorded',
        meta: `${shortPollster(high.pollster)}, ${fmtDate(high.date)}`,
      });
    }
    if (longestGap > 0) {
      out.push({
        value: `${longestGap} days`,
        label: 'Longest stretch without a published poll',
        meta: `ended ${fmtDate(gapEndDate)}`,
      });
    }
    return out;
  }, [polls]);

  if (stats.length === 0) return null;

  return (
    <section className="by-numbers">
      <h2 className="surveys-page__h2">By the numbers</h2>
      <div className="by-numbers__grid">
        {stats.map((s) => (
          <div key={s.label} className="by-numbers__card">
            <div className="by-numbers__value">{s.value}</div>
            <div className="by-numbers__label">{s.label}</div>
            {s.meta && <div className="by-numbers__meta">{s.meta}</div>}
          </div>
        ))}
      </div>
    </section>
  );
}
